import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import { checkAuth, initiateOAuth } from '../services/authService';

/**
 * ProtectedRoute component for admin pages
 * 
 * Features:
 * 1. Checks for Google OAuth authentication via sessionStorage (synchronous)
 * 2. Validates the stored token is still valid against Google Drive API (asynchronous)
 * 3. Attempts token refresh if expired
 * 4. Redirects to OAuth login if not authenticated
 */
const ProtectedRoute = ({ children }) => {
  const [authState, setAuthState] = useState({
    checking: true,
    authenticated: false,
  });

  useEffect(() => {
    let mounted = true;

    const verifyAuth = async () => {
      const result = await checkAuth();

      if (!mounted) return;

      if (result.authenticated) {
        setAuthState({ checking: false, authenticated: true });
      } else {
        // Clear any stale auth data
        sessionStorage.removeItem('admin_authenticated');
        sessionStorage.removeItem('admin_email');
        localStorage.removeItem('google_drive_access_token');
        setAuthState({ checking: false, authenticated: false });
      }
    };

    verifyAuth();

    return () => {
      mounted = false;
    };
  }, []);

  // Show loading while checking auth
  if (authState.checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Đang xác thực...</p>
          <p className="text-gray-400 text-sm mt-1">Kiểm tra phiên đăng nhập</p>
        </div>
      </div>
    );
  }

  // Not authenticated — redirect to OAuth login
  if (!authState.authenticated) {
    // Use a redirect component that will trigger OAuth after render
    return <OAuthRedirect />;
  }

  // Authenticated — wrap children with AdminSidebar layout
  return <AdminSidebar>{children}</AdminSidebar>;
};

/**
 * Component that redirects to OAuth login with a meaningful UI
 */
const OAuthRedirect = () => {
  useEffect(() => {
    // Small delay to let the UI render before redirecting
    const timer = setTimeout(() => {
      initiateOAuth();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Phiên đăng nhập hết hạn</h2>
        <p className="text-gray-600 mb-6">Vui lòng đăng nhập lại bằng Google để tiếp tục.</p>
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-gray-400 text-sm mt-3">Đang chuyển hướng đến Google...</p>
      </div>
    </div>
  );
};

export default ProtectedRoute;